import React, { useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { editCustomerFormSchema, EditCustomerFormValues } from "./schema";
import { editFormFields } from "./config";
import { FieldRenderer } from "../../../components/form/FieldRenderer";
import { EditCustomerFormProps } from "./types";
import { updateCustomer } from "../services/customerService";
import {
  useCountryList,
  useStateList,
  useCityList,
} from "../../../hooks/useLocationData";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import {
  setLoading,
  setError,
  setNotification,
  clearError,
  clearNotification,
} from "../../msp/slices/mspSlice";
import { EditWhiteIcon } from "../../common/icons";

type NamedItem = { id: number | string; name: string };

const EditCustomerInformationForm: React.FC<EditCustomerFormProps> = ({
  customer,
  onSuccess,
  onCancel,
}) => {
  const dispatch = useAppDispatch();
  const { loading, notification } = useAppSelector((state) => state.msp);

  const [formInitialized, setFormInitialized] = useState(false);

  const {
    countryList: countries,
    isCountryLoading,
    countryLoadingError,
  } = useCountryList();

  const hasInitialized = useRef(false);
  const isInitializing = useRef(false);

  const primaryAddress = useMemo(
    () => customer?.customerContacts?.[0]?.contact?.addresses?.[0] ?? null,
    [customer]
  );

  const findIdByName = (items: NamedItem[], name?: string): string => {
    if (!name || items.length === 0) return "";
    const norm = (s: string) => s.toLowerCase().trim();
    const item = items.find((it) => norm(it.name) === norm(name));
    return item?.id?.toString() ?? "";
  };

  const defaultValues = useMemo((): EditCustomerFormValues => {
    if (!customer) {
      return {
        company_name: "",
        website: "",
        company_phone: "",
        company_email: "",
        tax_id: "",
        company_street_address: "",
        company_city_id: "",
        company_state_id: "",
        company_country_id: "",
        company_postal_code: "",
      };
    }

    return {
      company_name: customer.organization_name || "",
      website: customer.organization_domain || "",
      company_phone: customer.mobile_phone || "",
      company_email: customer.work_email_address || "",
      tax_id: customer.tax_id || "",
      company_street_address: primaryAddress?.address || "",
      company_postal_code: primaryAddress?.postal_code || "",
      // prefill after lists load
      company_country_id: "",
      company_state_id: "",
      company_city_id: "",
    };
  }, [customer, primaryAddress]);

  const methods = useForm<EditCustomerFormValues>({
    resolver: zodResolver(editCustomerFormSchema) as any,
    defaultValues,
    mode: "onChange",
    shouldUnregister: false,
  });

  const { handleSubmit, watch, setValue, formState, reset } = methods;

  const watchCountry = watch("company_country_id");
  const watchState = watch("company_state_id");

  const selectedCountryId = useMemo(() => {
    const n = Number(watchCountry);
    return Number.isFinite(n) && n > 0 ? n : undefined;
  }, [watchCountry]);

  const selectedStateId = useMemo(() => {
    const n = Number(watchState);
    return Number.isFinite(n) && n > 0 ? n : undefined;
  }, [watchState]);

  const {
    stateList: states,
    isStateLoading,
    stateLoadingError,
  } = useStateList(selectedCountryId);

  const {
    cityList: cities,
    isCityLoading,
    cityLoadingError,
  } = useCityList(selectedStateId);

  // Reset when customer changes (important if modal re-opens without unmount)
  useEffect(() => {
    hasInitialized.current = false;
    isInitializing.current = false;
    setFormInitialized(false);
    reset(defaultValues);
  }, [customer?.id, defaultValues, reset]);

  // Aggregate API errors
  useEffect(() => {
    const combinedError =
      countryLoadingError || stateLoadingError || cityLoadingError || null;

    if (combinedError) dispatch(setError(combinedError));
    else dispatch(clearError());
  }, [countryLoadingError, stateLoadingError, cityLoadingError, dispatch]);

  // Auto-clear notification
  useEffect(() => {
    if (!notification) return;
    const timer = setTimeout(() => dispatch(clearNotification()), 5000);
    return () => clearTimeout(timer);
  }, [notification, dispatch]);

  // Initialize country from existing address (once)
  useEffect(() => {
    if (
      hasInitialized.current ||
      formInitialized ||
      countries.length === 0 ||
      !primaryAddress?.country?.name
    ) {
      return;
    }

    const countryId = findIdByName(
      countries as unknown as NamedItem[],
      primaryAddress.country.name
    );

    if (countryId) {
      setValue("company_country_id", countryId, {
        shouldValidate: true,
        shouldDirty: false,
      });
    }

    hasInitialized.current = true;
  }, [countries, primaryAddress, setValue, formInitialized]);

  // Country -> clear dependent fields (state + city)
  useEffect(() => {
    if (!watchCountry) return;
    setValue("company_state_id", "");
    setValue("company_city_id", "");
  }, [watchCountry, setValue]);

  // Prefill state (during init)
  useEffect(() => {
    if (
      formInitialized ||
      !watchCountry ||
      states.length === 0 ||
      !primaryAddress?.state?.name
    ) {
      return;
    }

    const stateId = findIdByName(
      states as unknown as NamedItem[],
      primaryAddress.state.name
    );

    if (stateId) {
      isInitializing.current = true;
      setValue("company_state_id", stateId, {
        shouldValidate: true,
        shouldDirty: false,
      });
    }
  }, [states, primaryAddress, watchCountry, setValue, formInitialized]);

  // State -> clear dependent field (city)
  useEffect(() => {
    if (!watchState) return;
    setValue("company_city_id", "");
  }, [watchState, setValue]);

  // Prefill city (during init)
  useEffect(() => {
    if (
      formInitialized ||
      !watchState ||
      cities.length === 0 ||
      !primaryAddress?.city?.name
    ) {
      return;
    }

    const cityId = findIdByName(
      cities as unknown as NamedItem[],
      primaryAddress.city.name
    );

    if (cityId) {
      setValue("company_city_id", cityId, {
        shouldValidate: true,
        shouldDirty: false,
      });

      setTimeout(() => {
        setFormInitialized(true);
        isInitializing.current = false;
      }, 100);
    }
  }, [cities, primaryAddress, watchState, setValue, formInitialized]);

  // Build select options
  const updatedFields = useMemo(() => {
    return editFormFields.map((field) => {
      if (field.name === "company_country_id") {
        return {
          ...field,
          options: [
            { label: "-- Select Country --", value: "" },
            ...countries.map((country: any) => ({
              label: country.name,
              value: country.id.toString(),
            })),
          ],
          loading: isCountryLoading,
        };
      }

      if (field.name === "company_state_id") {
        return {
          ...field,
          options: [
            { label: "-- Select State --", value: "" },
            ...states.map((state: any) => ({
              label: state.name,
              value: state.id.toString(),
            })),
          ],
          loading: isStateLoading,
          disabled: !watchCountry || isStateLoading,
        };
      }

      if (field.name === "company_city_id") {
        return {
          ...field,
          options: [
            { label: "-- Select City --", value: "" },
            ...cities.map((city: any) => ({
              label: city.name,
              value: city.id.toString(),
            })),
          ],
          loading: isCityLoading,
          disabled: !watchState || isCityLoading,
        };
      }

      return field;
    });
  }, [
    countries,
    states,
    cities,
    isCountryLoading,
    isStateLoading,
    isCityLoading,
    watchCountry,
    watchState,
  ]);

  const onSubmit = async (data: EditCustomerFormValues) => {
    if (!formInitialized || isInitializing.current) {
      dispatch(
        setNotification({
          message: "Please wait while we load location data...",
          type: "error",
        })
      );
      return;
    }

    try {
      dispatch(setLoading(true));
      dispatch(clearError());

      const apiPayload = {
        company_name: data.company_name.trim(),
        website: data.website || "",
        contact_no: data.company_phone || "",
        email_id: data.company_email.trim().toLowerCase() || "",
        street: data.company_street_address || "",
        country: data.company_country_id
          ? Number.parseInt(data.company_country_id, 10)
          : 0,
        state: data.company_state_id
          ? Number.parseInt(data.company_state_id, 10)
          : 0,
        city: data.company_city_id
          ? Number.parseInt(data.company_city_id, 10)
          : 0,
        zip_code: data.company_postal_code || "",
        tax_id: data.tax_id || "",
      };

      const response = await updateCustomer(customer?.id, apiPayload);

      dispatch(
        setNotification({
          message:
            response.message || "Customer information updated successfully!",
          type: "success",
        })
      );

      onSuccess();
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Failed to update customer information";

      dispatch(setError(errorMessage));
      dispatch(setNotification({ message: errorMessage, type: "error" }));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const isFormLoading = !formInitialized || isInitializing.current;

  return (
    <div className="w-full h-full bg-gray-50">
      <div className="mx-auto bg-white shadow-md rounded-md">
        <div className="px-6 py-4">
          <h2 className="text-xl font-semibold text-[#4F4F4F]">
            Edit Customer Information
          </h2>
        </div>

        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="px-6 py-6">
              <h3 className="text-lg font-medium text-[#000000] mb-4">
                Company Information
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {updatedFields.map((field) => (
                  <FieldRenderer key={field.name} cfg={field} />
                ))}
              </div>
            </div>

            <div className="px-6 py-4 flex justify-center gap-4">
              <button
                type="button"
                onClick={onCancel}
                className="px-8 py-2.5 rounded-md text-sm font-semibold text-gray-800 bg-[#E0E0E0] hover:bg-[#D5D5D5] focus:outline-none transition"
                disabled={loading || isFormLoading}
              >
                CANCEL
              </button>

              <button
                type="submit"
                disabled={loading || isFormLoading || !formState.isValid}
                className="px-8 py-2 rounded-sm text-sm font-semibold flex items-center justify-center gap-2 text-white bg-[#1E64E4] hover:bg-[#155BCA] focus:outline-none transition disabled:opacity-60"
              >
                <span className="w-5 h-5">
                  <EditWhiteIcon />
                </span>
                {loading ? "UPDATING..." : "UPDATE"}
              </button>
            </div>
          </form>
        </FormProvider>
      </div>
    </div>
  );
};

export default EditCustomerInformationForm;
